import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Heading, Menu, ResponsiveContext, Tab, Tabs, Text, TextInput } from "grommet";
import * as React from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import LinkAnchor from "../components/LinkAnchor";
import StyledModal from "../components/StyledModel";
import CreateGame from "./CreateGame";
import { GameCard } from "./GameCard";
import GameProfile from "./GameProfile";
import { getGames, GameViewModel } from "./GamesStore";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
// @ts-ignore
const bulkPlus = <FontAwesomeIcon size="xl" icon={["fad", "layer-plus"]}></FontAwesomeIcon>;
// @ts-ignore
const gamePlus = <FontAwesomeIcon size="xl" icon={["fad", "users-medical"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const search = <FontAwesomeIcon icon={["fad", "search"]}></FontAwesomeIcon>;

export const GameRow = ({ game, children, size = "medium" }: { game: GameViewModel; children?: React.ReactElement; size?: string }) => {
    let BoxProps = {};

    const navigate = useNavigate();

    if (!children) {
        BoxProps = {
            hoverIndicator: "background-back",
            onClick: () => {
                navigate(`${game.id}`);
            },
        };
    }

    return (
        <Box key={game.id} background={"background"} pad={size} direction="row" gap="small" {...BoxProps}>
            <Box justify="center">
                <img src={game.logoUri} width="35px" alt="" />
            </Box>
            <Box justify="between" direction="row" width="100%">
                <Box justify="center">
                    <LinkAnchor path={`${game.id}`} label={game.name}></LinkAnchor>
                </Box>
                <Box justify="center">{children}</Box>
            </Box>
        </Box>
    );
};

export const GameTile = ({ game }: { game: GameViewModel }) => {
    return (
        <Link to={`/games/${game.id}`} style={{ textDecoration: "none" }}>
            <Box key={game.id} background={"background-back"} pad="medium" gap="small" height="auto" justify="between" fill="vertical" hoverIndicator="background-front" onClick={() => {}}>
                <Box align="center" justify="center">
                    <img src={game.logoUri} width="25px" alt="" />
                </Box>
                <Box>
                    <Box align="center" justify="center">
                        {game.name}
                    </Box>
                </Box>
            </Box>
        </Link>
    );
};

export const GameBlock = ({ game, size, children }: { game: GameViewModel; size?: string; children?: React.ReactElement }) => {
    let { pathname } = useLocation();
    const navigate = useNavigate();

    const logoSize = size ?? "150px";

    return (
        <Link to={`/games/${game.id}`} style={{ textDecoration: "none" }}>
            <Box key={game.id} background={"background-back"} pad="medium" gap="small" height="auto" justify="between" fill="vertical" hoverIndicator="background-front" onClick={() => {}}>
                <Box align="center" justify="center">
                    <img src={game.logoUri} width={logoSize} alt="" />
                </Box>
                <Box>
                    <Box align="center" justify="center">
                        {game.name}
                    </Box>
                    {children && children}
                </Box>
            </Box>
        </Link>
    );
};

export const GameMenu = ({ game }) => {
    const size = React.useContext(ResponsiveContext);
    const navigate = useNavigate();
    return (
        <>
            {size == "small" && (
                <Menu
                    items={[
                        {
                            label: "Profile",
                            onClick: () => {
                                navigate("/games/" + game.id);
                            },
                        },
                        {
                            label: "Home Page",
                            onClick: () => {
                                //@ts-ignore
                                window.location = "/ue/games/" + game.id;
                            },
                        },
                    ]}
                />
            )}
            {size != "small" && (
                <Box direction="row" gap="small">
                    <CustomButton primary path={"/games/" + game.id} icon={edit} label="Profile"></CustomButton>
                    <CustomButton primary href={"/ue/games/" + game.id} icon={idBadge} label="Home Page"></CustomButton>
                </Box>
            )}
        </>
    );
};

export const GameGrid = ({ games }) => {
    const size = React.useContext(ResponsiveContext);

    const BracketGridProps = size == "small" ? { rows: ["auto"], columns: ["1/2", "1/2"] } : { rows: ["auto"], columns: ["1/4", "1/4", "1/4", "1/4"] };

    return (
        <>
            <Box direction="row" gap="medium" wrap justify="center">
                {games.map((game) => (
                    <Box margin={{ bottom: "medium" }} key={game.id}>
                        <Link to={`${game.id}`} style={{ textDecoration: "none", color: "unset" }}>
                            <GameCard game={game}></GameCard>
                        </Link>
                    </Box>
                    // <GameBlock game={game} key={game.id}></GameBlock>
                ))}
            </Box>
        </>
    );
};

const GamesManagement = () => {
    const user = useUser();

    const [games, setGames] = React.useState([] as GameViewModel[]);
    const [filter, setFilter] = React.useState("");
    const [filteredGames, setFilteredGames] = React.useState([] as GameViewModel[]);
    let { pathname } = useLocation();

    React.useEffect(() => {
        getGames().then((games) => {
            setGames(games);
        });
    }, [pathname, user]);

    React.useEffect(() => {
        if (filter) {
            setFilteredGames(games.filter((t) => t.name.toLowerCase().indexOf(filter.toLowerCase()) != -1));
        } else {
            setFilteredGames(games);
        }
    }, [games, filter]);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const hasWritePermission = usePermission("sport:games.write");

    const [showGamesCreateModal, setShowGamesCreateModal] = React.useState(false);
    const [showGamesBulkCreateModal, setShowGamesBulkCreateModal] = React.useState(false);

    return (
        <div>
            <Routes>
                <Route path={"create"} element={<>{hasWritePermission && <CreateGame />}</>}></Route>
                <Route path={":id/*"} element={<GameProfile />}></Route>
                <Route
                    path={""}
                    element={
                        <>
                            {/* <Heading size="small" textAlign="center" fill>
                                Games
                            </Heading> */}
                            <Box direction="row" gap="small" justify="between">
                                {hasWritePermission && (
                                    <Box direction="row" gap="small">
                                        <CustomButton onClick={() => setShowGamesCreateModal(true)} icon={gamePlus} label="Add Game"></CustomButton>
                                    </Box>
                                )}
                                <Box direction="row" justify="end" gap="small" align="center">
                                    <Box pad="small">
                                        <TextInput icon={search} placeholder="Search..." value={filter} onChange={(e) => setFilter(e.target.value)}></TextInput>
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(0)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "grid-2"]} />
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(1)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "list"]} />
                                    </Box>
                                </Box>
                            </Box>
                            <Tabs activeIndex={index} onActive={onActive}>
                                <Tab>
                                    <GameGrid games={filteredGames}></GameGrid>
                                </Tab>
                                <Tab>
                                    <Box gap="small">
                                        {filteredGames.map((game) => (
                                            <GameRow game={game} key={game.id}>
                                                <Box direction="row" gap="small">
                                                    <GameMenu game={game}></GameMenu>
                                                </Box>
                                            </GameRow>
                                        ))}
                                    </Box>
                                </Tab>
                            </Tabs>
                            <Box pad={{ top: "small" }}>
                                {games.length != filteredGames.length && (
                                    <Text>
                                        Showing {filteredGames.length} of {games.length}{" "}
                                        <Anchor href="#" onClick={() => setFilter("")}>
                                            Show All
                                        </Anchor>
                                    </Text>
                                )}
                            </Box>

                            <StyledModal isOpen={showGamesCreateModal} setIsOpen={() => setShowGamesCreateModal(false)}>
                                <CreateGame />
                            </StyledModal>
                        </>
                    }
                ></Route>
            </Routes>
        </div>
    );
};

export default GamesManagement;
